import { InventorySheet } from "@/components/operations";
import {
  buildInventoryWorkspacePath,
  formatStatusLabel,
  getInventoryOperationalStatus,
} from "@/components/operations/operations-shared";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchableSelect } from "@/components/ui/searchable-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import {
  deriveAccountStatus,
  poPaymentTimingSchema,
  type POPaymentTiming,
} from "@/core";
import {
  useAllInventories,
  useAllVendors,
  useCreateInventory,
  useGetProducts,
  useNoteTemplates,
  useAllPurchaseOrders,
} from "@/hooks";
import { cn } from "@/lib/utils";
import {
  getVendorSearchKeywords,
  getVendorSelectLabel,
  timestampToDate,
} from "@/utils";
import {
  exportInventoryToCsv,
  exportInventoryToExcel,
} from "@/utils/csv-export";
import { getPurchaseOrderTotal } from "@/utils/order-total";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronDown,
  ChevronRight,
  Download,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

type InventoryStatus =
  | "draft"
  | "active"
  | "depleted"
  | "closed"
  | "blocked"
  | "archived";

const ALL_INVENTORY_STATUSES: InventoryStatus[] = [
  "draft",
  "active",
  "depleted",
  "blocked",
  "closed",
  "archived",
];
const DEFAULT_SELECTED_STATUSES = new Set<InventoryStatus>([
  "draft",
  "active",
  "depleted",
  "blocked",
  "closed",
]);
type PaymentArrangementFilterValue = POPaymentTiming | "none";
const ALL_PAYMENT_ARRANGEMENTS: PaymentArrangementFilterValue[] = [
  ...poPaymentTimingSchema.options,
  "none",
];
const formatPaymentArrangementLabel = (
  value: PaymentArrangementFilterValue,
) => (value === "none" ? "Not Set" : formatStatusLabel(value));
// The dropdown sets the admin-controlled base status; the effective accountStatus
// (incl. on_hold / written_off / needs_reset) is derived by the recompute trigger.
type ManualAccountStatus = "active" | "restricted" | "closed";
type TwoFaStatus =
  | "none"
  | "email_forwarding"
  | "otp_login"
  | "otp_booking"
  | "otp_authenticator"
  | "ah_to_book"
  | "no_need_to_check"
  | "agent";
type InventorySortKey =
  | "updatedAt"
  | "productName"
  | "buyCpm"
  | "accountTier"
  | "availableMiles";
type SortDirection = "asc" | "desc";

interface SortState {
  key: InventorySortKey;
  direction: SortDirection;
}

function SortableHeader(payload: {
  label: string;
  sortKey: InventorySortKey;
  sortState: SortState;
  onSort: (key: InventorySortKey) => void;
}) {
  const { label, sortKey, sortState, onSort } = payload;
  const isActive = sortState.key === sortKey;

  return (
    <Button
      variant="ghost"
      onClick={() => onSort(sortKey)}
      className="h-auto p-0 font-semibold hover:bg-transparent"
    >
      {label}
      {isActive ? (
        sortState.direction === "asc" ? (
          <ArrowUp className="ml-2 h-4 w-4" />
        ) : (
          <ArrowDown className="ml-2 h-4 w-4" />
        )
      ) : (
        <ArrowUpDown className="ml-2 h-4 w-4" />
      )}
    </Button>
  );
}

function abbreviateProductName(name: string): string {
  const airlinePart = name.split("/")[0].trim();
  return airlinePart
    .split(/\s+/)
    .map((word) => word[0])
    .join("")
    .toUpperCase();
}

function formatBuyCpm(value: number | undefined): string {
  return value === undefined ? "-" : (value * 100).toFixed(5);
}

export default function InventoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedInventoryId = searchParams.get("inventoryId") || "";

  const inventoriesQuery = useAllInventories();
  // Every PO, not just the newest page: each inventory row reads its payment
  // arrangement and buy CPM from its own POs, however old they are.
  const { purchaseOrders } = useAllPurchaseOrders();
  const vendorsQuery = useAllVendors();
  const productsQuery = useGetProducts();
  const createInventoryMutation = useCreateInventory();
  const noteTemplatesQuery = useNoteTemplates();

  const [inventoryName, setInventoryName] = useState("");
  const [productId, setProductId] = useState("");
  const [inventoryStatus, setInventoryStatus] =
    useState<InventoryStatus>("draft");
  const [accountOwnerName, setAccountOwnerName] = useState("");
  const [ownerVendorId, setOwnerVendorId] = useState("");
  const [accountEmail, setAccountEmail] = useState("");
  const [accountPassword, setAccountPassword] = useState("");
  const [manualAccountStatus, setManualAccountStatus] =
    useState<ManualAccountStatus>("active");
  const [twoFaStatus, setTwoFaStatus] = useState<TwoFaStatus>("none");
  const [statusTierName, setStatusTierName] = useState("");
  const [statusTierPercentage, setStatusTierPercentage] = useState("");
  const [dob, setDob] = useState("");
  const [ipLocation, setIpLocation] = useState("");
  const [paypalEmail, setPaypalEmail] = useState("");
  const [availableMiles, setAvailableMiles] = useState("0");
  const [cardTypeTag, setCardTypeTag] = useState("");
  const [gologinProfileId, setGologinProfileId] = useState("");
  const [username, setUsername] = useState("");
  const [expirationInfo, setExpirationInfo] = useState("");
  const [notes, setNotes] = useState("");
  const [isCreateFormExpanded, setIsCreateFormExpanded] = useState(false);
  const [selectedInventoryIds, setSelectedInventoryIds] = useState<Set<string>>(
    new Set(),
  );
  const [productFilter, setProductFilter] = useState("all");
  const [accountTierFilter, setAccountTierFilter] = useState("all");
  const [selectedStatuses, setSelectedStatuses] = useState<
    Set<InventoryStatus>
  >(DEFAULT_SELECTED_STATUSES);
  const [selectedPaymentArrangements, setSelectedPaymentArrangements] =
    useState<Set<PaymentArrangementFilterValue>>(
      () => new Set(ALL_PAYMENT_ARRANGEMENTS),
    );
  const [usernameSearch, setUsernameSearch] = useState("");
  const [sortState, setSortState] = useState<SortState>({
    key: "updatedAt",
    direction: "desc",
  });

  const inventories = useMemo(
    () => inventoriesQuery.data?.pages.flat() ?? [],
    [inventoriesQuery.data],
  );
  const vendors = useMemo(
    () => vendorsQuery.data?.pages.flat() ?? [],
    [vendorsQuery.data],
  );
  const products = useMemo(
    () => productsQuery.data ?? [],
    [productsQuery.data],
  );
  const selectedProduct = products.find((product) => product.id === productId);
  const productCodeById = useMemo(() => {
    const map = new Map<string, string>();
    for (const product of products) {
      if (product.id && product.productCode) {
        map.set(product.id, product.productCode);
      }
    }
    return map;
  }, [products]);
  const vendorOptions = useMemo(
    () =>
      [...vendors]
        .sort((left, right) =>
          getVendorSelectLabel({ vendor: left }).localeCompare(
            getVendorSelectLabel({ vendor: right }),
          ),
        )
        .map((vendor) => ({
          value: vendor.id,
          label: getVendorSelectLabel({ vendor }),
          keywords: getVendorSearchKeywords({ vendor }),
        })),
    [vendors],
  );
  const inventoryBuyCpmMap = useMemo(() => {
    const purchaseTotalsByInventoryId = new Map<
      string,
      { miles: number; totalAmount: number }
    >();

    for (const purchaseOrder of purchaseOrders) {
      if (!purchaseOrder.inventoryId) continue;
      const existingTotals = purchaseTotalsByInventoryId.get(
        purchaseOrder.inventoryId,
      ) ?? { miles: 0, totalAmount: 0 };

      purchaseTotalsByInventoryId.set(purchaseOrder.inventoryId, {
        miles: existingTotals.miles + purchaseOrder.amountBought,
        totalAmount:
          existingTotals.totalAmount +
          getPurchaseOrderTotal({
            buyPrice: purchaseOrder.buyPrice,
            taxCost: purchaseOrder.taxCost,
            totalAmount: purchaseOrder.totalAmount,
          }),
      });
    }

    return new Map(
      inventories.map((inventory) => {
        const purchaseTotals = purchaseTotalsByInventoryId.get(inventory.id);
        const buyCpm =
          purchaseTotals && purchaseTotals.miles > 0
            ? purchaseTotals.totalAmount / purchaseTotals.miles
            : inventory.sourceDealSnapshot?.unitPrice;

        return [inventory.id, buyCpm] as const;
      }),
    );
  }, [inventories, purchaseOrders]);
  const inventoryPaymentTimingMap = useMemo(() => {
    const timingByInventoryId = new Map<string, string>();

    for (const purchaseOrder of purchaseOrders) {
      if (!purchaseOrder.inventoryId || !purchaseOrder.paymentTiming) continue;
      if (!timingByInventoryId.has(purchaseOrder.inventoryId)) {
        timingByInventoryId.set(
          purchaseOrder.inventoryId,
          purchaseOrder.paymentTiming,
        );
      }
    }

    return timingByInventoryId;
  }, [purchaseOrders]);
  const accountTierOptions = useMemo(
    () =>
      Array.from(
        new Set(
          inventories
            .map((inventory) => inventory.statusTier?.name?.trim())
            .filter((tierName): tierName is string => Boolean(tierName)),
        ),
      ).sort((left, right) => left.localeCompare(right)),
    [inventories],
  );
  const vendorsByOwnerLabel = useMemo(() => {
    const map = new Map<string, (typeof vendors)[number]>();
    for (const vendor of vendors) {
      map.set(getVendorSelectLabel({ vendor }), vendor);
    }
    return map;
  }, [vendors]);
  const filteredInventories = useMemo(() => {
    const normalizedUsernameSearch = usernameSearch.trim().toLowerCase();
    return inventories.filter((inventory) => {
      if (productFilter !== "all" && inventory.productId !== productFilter) {
        return false;
      }

      const accountTier = inventory.statusTier?.name?.trim() || "none";
      if (accountTierFilter !== "all" && accountTier !== accountTierFilter) {
        return false;
      }

      if (selectedStatuses.size < ALL_INVENTORY_STATUSES.length) {
        const operationalStatus = getInventoryOperationalStatus({ inventory });
        if (!selectedStatuses.has(operationalStatus)) {
          return false;
        }
      }

      if (
        selectedPaymentArrangements.size < ALL_PAYMENT_ARRANGEMENTS.length
      ) {
        const paymentArrangement = (inventoryPaymentTimingMap.get(
          inventory.id,
        ) ?? "none") as PaymentArrangementFilterValue;
        if (!selectedPaymentArrangements.has(paymentArrangement)) {
          return false;
        }
      }

      if (normalizedUsernameSearch) {
        const ownerVendor = inventory.accountOwnerName
          ? vendorsByOwnerLabel.get(inventory.accountOwnerName)
          : undefined;
        const haystack = [
          inventory.username,
          inventory.accountOwnerName,
          inventory.accountEmail,
          ownerVendor?.phone,
          ownerVendor?.secondaryPhone,
          ownerVendor?.email,
          ownerVendor?.secondaryEmail,
        ]
          .filter((value): value is string => Boolean(value))
          .join(" ")
          .toLowerCase();

        if (!haystack.includes(normalizedUsernameSearch)) {
          return false;
        }
      }

      return true;
    });
  }, [
    accountTierFilter,
    inventories,
    inventoryPaymentTimingMap,
    selectedPaymentArrangements,
    selectedStatuses,
    productFilter,
    usernameSearch,
    vendorsByOwnerLabel,
  ]);
  const sortedInventories = useMemo(() => {
    const directionMultiplier = sortState.direction === "asc" ? 1 : -1;
    const nextInventories = [...filteredInventories];

    nextInventories.sort((left, right) => {
      if (sortState.key === "productName") {
        return (
          left.productName.localeCompare(right.productName) *
          directionMultiplier
        );
      }

      if (sortState.key === "buyCpm") {
        const leftBuyCpm = inventoryBuyCpmMap.get(left.id) ?? Number.MAX_VALUE;
        const rightBuyCpm =
          inventoryBuyCpmMap.get(right.id) ?? Number.MAX_VALUE;

        return (leftBuyCpm - rightBuyCpm) * directionMultiplier;
      }

      if (sortState.key === "accountTier") {
        const leftTier = left.statusTier?.name?.trim() || "";
        const rightTier = right.statusTier?.name?.trim() || "";

        return leftTier.localeCompare(rightTier) * directionMultiplier;
      }

      if (sortState.key === "availableMiles") {
        return (
          (left.availableMiles - right.availableMiles) * directionMultiplier
        );
      }

      return (
        (left.updatedAt.getTime() - right.updatedAt.getTime()) *
        directionMultiplier
      );
    });

    return nextInventories;
  }, [filteredInventories, inventoryBuyCpmMap, sortState]);
  const selectedInventories = useMemo(
    () =>
      sortedInventories.filter((inventory) =>
        selectedInventoryIds.has(inventory.id),
      ),
    [selectedInventoryIds, sortedInventories],
  );
  const allVisibleInventoriesSelected =
    sortedInventories.length > 0 &&
    sortedInventories.every((inventory) =>
      selectedInventoryIds.has(inventory.id),
    );
  const someVisibleInventoriesSelected =
    !allVisibleInventoriesSelected &&
    sortedInventories.some((inventory) =>
      selectedInventoryIds.has(inventory.id),
    );

  const toggleInventorySelection = (inventoryId: string, checked: boolean) => {
    setSelectedInventoryIds((previous) => {
      const next = new Set(previous);
      if (checked) {
        next.add(inventoryId);
      } else {
        next.delete(inventoryId);
      }
      return next;
    });
  };

  const toggleAllVisibleInventoriesSelection = (checked: boolean) => {
    setSelectedInventoryIds((previous) => {
      const next = new Set(previous);

      sortedInventories.forEach((inventory) => {
        if (checked) {
          next.add(inventory.id);
        } else {
          next.delete(inventory.id);
        }
      });

      return next;
    });
  };

  const handleSort = (key: InventorySortKey) => {
    setSortState((current) => {
      if (current.key === key) {
        return {
          key,
          direction: current.direction === "asc" ? "desc" : "asc",
        };
      }

      return {
        key,
        direction: key === "updatedAt" ? "desc" : "asc",
      };
    });
  };

  const handleCreateInventory = async () => {
    if (!inventoryName.trim() || !selectedProduct) {
      return;
    }

    const normalizedAvailableMiles = Math.max(
      Number.parseInt(availableMiles || "0", 10) || 0,
      0,
    );
    const normalizedStatusTierPercentage = Number(statusTierPercentage);

    const inventoryId = await createInventoryMutation.mutateAsync({
      data: {
        name: inventoryName.trim(),
        productId: selectedProduct.id,
        productName: selectedProduct.name,
        status: inventoryStatus,
        bookingBlockedUntil: null,
        bookingBlockedBySalesOrderId: null,
        accountOwnerName: accountOwnerName.trim() || undefined,
        accountEmail: accountEmail.trim() || undefined,
        accountPassword: accountPassword.trim() || undefined,
        paypalEmail: paypalEmail.trim() || undefined,
        statusTier:
          statusTierName.trim() &&
          Number.isFinite(normalizedStatusTierPercentage)
            ? {
                name: statusTierName.trim(),
                percentage: normalizedStatusTierPercentage,
              }
            : undefined,
        dob: dob ? new Date(dob) : undefined,
        ipLocation: ipLocation.trim() || undefined,
        manualAccountStatus,
        // The effective status is reconciled by the recompute trigger; derive an
        // initial value inline so a freshly-created account is correct immediately.
        accountStatus: deriveAccountStatus({
          manualAccountStatus,
          problemStatus: undefined,
          accountValue: 0,
          availableMiles: 0,
          buyCpm: null,
          hasCredentials: Boolean(
            accountEmail.trim() && accountPassword.trim(),
          ),
        }),
        accountValue: null,
        buyCpm: null,
        twoFaStatus,
        cardTypeTag: cardTypeTag.trim() || undefined,
        gologinProfileId: gologinProfileId.trim() || null,
        username: username.trim() || undefined,
        expirationInfo: expirationInfo.trim() || undefined,
        notes: notes.trim() || undefined,
        totalPurchasedMiles: normalizedAvailableMiles,
        totalSoldMiles: 0,
        availableMiles: normalizedAvailableMiles,
        accountBalance: null,
        purchaseOrderCount: 0,
        sellOrderCount: 0,
      },
    });

    setInventoryName("");
    setProductId("");
    setInventoryStatus("draft");
    setAccountOwnerName("");
    setOwnerVendorId("");
    setAccountEmail("");
    setAccountPassword("");
    setManualAccountStatus("active");
    setTwoFaStatus("none");
    setStatusTierName("");
    setStatusTierPercentage("");
    setDob("");
    setIpLocation("");
    setPaypalEmail("");
    setAvailableMiles("0");
    setCardTypeTag("");
    setGologinProfileId("");
    setUsername("");
    setExpirationInfo("");
    setNotes("");
    setSearchParams({ inventoryId }, { replace: true });
  };

  const handleOwnerVendorChange = (vendorId: string) => {
    const selectedVendor = vendors.find((vendor) => vendor.id === vendorId);

    setOwnerVendorId(vendorId);
    setAccountOwnerName(
      selectedVendor ? getVendorSelectLabel({ vendor: selectedVendor }) : "",
    );
    setAccountEmail(selectedVendor?.email || "");
  };

  return (
    <div
      className={cn(
        "container mx-auto flex max-w-7xl flex-col gap-6 px-4 py-8 sm:px-6 lg:px-8",
        isCreateFormExpanded
          ? "min-h-[calc(100svh-var(--header-height)-1rem)]"
          : "h-[calc(100svh-var(--header-height)-1rem)] overflow-hidden",
      )}
    >
      <Card>
        <CardHeader
          className="cursor-pointer select-none"
          onClick={() => setIsCreateFormExpanded((prev) => !prev)}
        >
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Inventory</CardTitle>
              <CardDescription>
                Inventories are the root operational structure. Create the
                inventory and its embedded account details together so the
                workspace is ready for purchase orders, sell orders, and
                financials.
              </CardDescription>
            </div>
            {isCreateFormExpanded ? (
              <ChevronDown className="h-5 w-5 text-muted-foreground shrink-0" />
            ) : (
              <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
            )}
          </div>
        </CardHeader>
        {isCreateFormExpanded && (
          <CardContent className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <div className="space-y-1">
              <Label>Inventory Name</Label>
              <Input
                value={inventoryName}
                onChange={(event) => setInventoryName(event.target.value)}
                placeholder="AA Summer inventory"
              />
            </div>
            <div className="space-y-1">
              <Label>Product</Label>
              <Select
                value={productId}
                onValueChange={(value) => {
                  setProductId(value);
                  const templates = noteTemplatesQuery.data ?? [];
                  const productTemplate = templates.find(
                    (t) => t.productId === value,
                  );
                  const defaultTemplate = templates.find((t) => !t.productId);
                  const matchedContent =
                    productTemplate?.content ?? defaultTemplate?.content;
                  if (matchedContent !== undefined) {
                    setNotes(matchedContent);
                  }
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select product" />
                </SelectTrigger>
                <SelectContent>
                  {products.map((product) => (
                    <SelectItem key={product.id} value={product.id}>
                      {product.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Inventory Status</Label>
              <Select
                value={inventoryStatus}
                onValueChange={(value) =>
                  setInventoryStatus(value as InventoryStatus)
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="depleted">Depleted</SelectItem>
                  <SelectItem value="blocked">Blocked</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Account Status</Label>
              <Select
                value={manualAccountStatus}
                onValueChange={(value) =>
                  setManualAccountStatus(value as ManualAccountStatus)
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="restricted">Restricted</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Account Owner</Label>
              <SearchableSelect
                value={ownerVendorId}
                onChange={handleOwnerVendorChange}
                placeholder={accountOwnerName || "Select vendor"}
                searchPlaceholder="Search vendor..."
                emptyText="No vendor found."
                options={vendorOptions}
              />
            </div>
            <div className="space-y-1">
              <Label>Account Email</Label>
              <Input
                value={accountEmail}
                onChange={(event) => setAccountEmail(event.target.value)}
                placeholder="account@example.com"
              />
            </div>
            <div className="space-y-1">
              <Label>Account Password</Label>
              <Input
                type="password"
                value={accountPassword}
                onChange={(event) => setAccountPassword(event.target.value)}
                placeholder="Password"
              />
            </div>
            <div className="space-y-1">
              <Label>2FA Status</Label>
              <Select
                value={twoFaStatus}
                onValueChange={(value) => setTwoFaStatus(value as TwoFaStatus)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="email_forwarding">
                    Email Forwarding
                  </SelectItem>
                  <SelectItem value="otp_login">OTP - Login</SelectItem>
                  <SelectItem value="otp_booking">OTP - Booking</SelectItem>
                  <SelectItem value="otp_authenticator">
                    OTP - Authenticator
                  </SelectItem>
                  <SelectItem value="ah_to_book">AH to Book</SelectItem>
                  <SelectItem value="no_need_to_check">
                    No Need to Check
                  </SelectItem>
                  <SelectItem value="agent">Agent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Status Tier</Label>
              <Input
                value={statusTierName}
                onChange={(event) => setStatusTierName(event.target.value)}
                placeholder="Gold"
              />
            </div>
            <div className="space-y-1">
              <Label>Status Tier %</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={statusTierPercentage}
                onChange={(event) =>
                  setStatusTierPercentage(event.target.value)
                }
                placeholder="15"
              />
            </div>
            <div className="space-y-1">
              <Label>DOB</Label>
              <Input
                type="date"
                value={dob}
                onChange={(event) => setDob(event.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label>IP Location</Label>
              <Input
                value={ipLocation}
                onChange={(event) => setIpLocation(event.target.value)}
                placeholder="Toronto, CA"
              />
            </div>
            <div className="space-y-1">
              <Label>PayPal Email</Label>
              <Input
                value={paypalEmail}
                onChange={(event) => setPaypalEmail(event.target.value)}
                placeholder="paypal@example.com"
              />
            </div>
            <div className="space-y-1">
              <Label>Available Miles</Label>
              <Input
                type="number"
                min="0"
                step="1"
                value={availableMiles}
                onChange={(event) => setAvailableMiles(event.target.value)}
                placeholder="0"
              />
            </div>
            <div className="space-y-1">
              <Label>Card Type Tag</Label>
              <Input
                value={cardTypeTag}
                onChange={(event) => setCardTypeTag(event.target.value)}
                placeholder="Visa Infinite"
              />
            </div>
            <div className="space-y-1">
              <Label>GoLogin Profile Id</Label>
              <Input
                value={gologinProfileId}
                onChange={(event) => setGologinProfileId(event.target.value)}
                placeholder="Optional profile id"
              />
            </div>
            <div className="space-y-1">
              <Label>User ID / Username</Label>
              <Input
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                placeholder="Account username or member ID"
              />
            </div>
            <div className="space-y-1 md:col-span-2 xl:col-span-2">
              <Label>Expiration Info</Label>
              <Textarea
                value={expirationInfo}
                onChange={(event) => setExpirationInfo(event.target.value)}
                placeholder="Expiration details, renewal dates, or related notes"
              />
            </div>
            <div className="space-y-1 md:col-span-2 xl:col-span-3">
              <Label>Notes</Label>
              <Textarea
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                placeholder="Operational notes, access context, or follow-up tasks"
              />
            </div>
            <div className="flex items-end">
              <Button
                onClick={(event) => {
                  event.stopPropagation();
                  handleCreateInventory();
                }}
                disabled={!inventoryName.trim() || !selectedProduct}
                loading={createInventoryMutation.isPending}
                loadingText="Creating..."
              >
                Create Inventory
              </Button>
            </div>
          </CardContent>
        )}
      </Card>

      <Card
        className={cn(
          "flex flex-col",
          isCreateFormExpanded ? "" : "min-h-0 flex-1",
        )}
      >
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle>Inventory List</CardTitle>
            <CardDescription>
              Open an inventory sheet first, then continue into the inventory
              workspace for spreadsheet-style operations management.
            </CardDescription>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                disabled={sortedInventories.length === 0}
              >
                <Download className="mr-2 h-4 w-4" />
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                disabled={selectedInventories.length === 0}
                onClick={() => exportInventoryToCsv(selectedInventories)}
              >
                Export Selected CSV
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={selectedInventories.length === 0}
                onClick={() => exportInventoryToExcel(selectedInventories)}
              >
                Export Selected Excel
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => exportInventoryToCsv(sortedInventories)}
              >
                Export All CSV
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => exportInventoryToExcel(sortedInventories)}
              >
                Export All Excel
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </CardHeader>
        <CardContent
          className={cn(
            "flex flex-col",
            isCreateFormExpanded ? "" : "min-h-0 flex-1",
          )}
        >
          <div className="mb-4 grid gap-3 md:grid-cols-3 xl:grid-cols-5">
            <div className="space-y-2">
              <Label>Product</Label>
              <Select value={productFilter} onValueChange={setProductFilter}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="All products" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Products</SelectItem>
                  {products.map((product) => (
                    <SelectItem key={product.id} value={product.id}>
                      {product.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Account Tier</Label>
              <Select
                value={accountTierFilter}
                onValueChange={setAccountTierFilter}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="All account tiers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Account Tiers</SelectItem>
                  <SelectItem value="none">No Tier</SelectItem>
                  {accountTierOptions.map((tierName) => (
                    <SelectItem key={tierName} value={tierName}>
                      {tierName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Inventory Status</Label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-between font-normal h-9 px-3 text-sm"
                  >
                    <span className="truncate">
                      {selectedStatuses.size === ALL_INVENTORY_STATUSES.length
                        ? "All Statuses"
                        : selectedStatuses.size ===
                              DEFAULT_SELECTED_STATUSES.size &&
                            !selectedStatuses.has("archived") &&
                            ALL_INVENTORY_STATUSES.filter(
                              (s) => s !== "archived",
                            ).every((s) => selectedStatuses.has(s))
                          ? "All (excl. Archived)"
                          : selectedStatuses.size === 0
                            ? "No Statuses"
                            : selectedStatuses.size === 1
                              ? formatStatusLabel([...selectedStatuses][0])
                              : `${selectedStatuses.size} statuses`}
                    </span>
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-48">
                  <DropdownMenuCheckboxItem
                    checked={
                      selectedStatuses.size === ALL_INVENTORY_STATUSES.length
                    }
                    onCheckedChange={(checked) =>
                      setSelectedStatuses(
                        new Set(checked ? ALL_INVENTORY_STATUSES : []),
                      )
                    }
                  >
                    All Statuses
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuSeparator />
                  {ALL_INVENTORY_STATUSES.map((status) => (
                    <DropdownMenuCheckboxItem
                      key={status}
                      checked={selectedStatuses.has(status)}
                      onCheckedChange={(checked) =>
                        setSelectedStatuses((prev) => {
                          const next = new Set(prev);
                          if (checked) next.add(status);
                          else next.delete(status);
                          return next;
                        })
                      }
                    >
                      {formatStatusLabel(status)}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <div className="space-y-2">
              <Label>Payment Arrangement</Label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-between font-normal h-9 px-3 text-sm"
                  >
                    <span className="truncate">
                      {selectedPaymentArrangements.size ===
                      ALL_PAYMENT_ARRANGEMENTS.length
                        ? "All Arrangements"
                        : selectedPaymentArrangements.size === 0
                          ? "No Arrangements"
                          : selectedPaymentArrangements.size === 1
                            ? formatPaymentArrangementLabel(
                                [...selectedPaymentArrangements][0],
                              )
                            : `${selectedPaymentArrangements.size} arrangements`}
                    </span>
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-52">
                  <DropdownMenuCheckboxItem
                    checked={
                      selectedPaymentArrangements.size ===
                      ALL_PAYMENT_ARRANGEMENTS.length
                    }
                    onCheckedChange={(checked) =>
                      setSelectedPaymentArrangements(
                        new Set(checked ? ALL_PAYMENT_ARRANGEMENTS : []),
                      )
                    }
                  >
                    All Arrangements
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuSeparator />
                  {ALL_PAYMENT_ARRANGEMENTS.map((arrangement) => (
                    <DropdownMenuCheckboxItem
                      key={arrangement}
                      checked={selectedPaymentArrangements.has(arrangement)}
                      onCheckedChange={(checked) =>
                        setSelectedPaymentArrangements((prev) => {
                          const next = new Set(prev);
                          if (checked) next.add(arrangement);
                          else next.delete(arrangement);
                          return next;
                        })
                      }
                    >
                      {formatPaymentArrangementLabel(arrangement)}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <div className="space-y-2">
              <Label>Search</Label>
              <Input
                value={usernameSearch}
                onChange={(event) => setUsernameSearch(event.target.value)}
                placeholder="Username, owner, email, or phone..."
              />
            </div>
          </div>
          <Table
            containerClassName={cn(
              "overflow-auto",
              isCreateFormExpanded ? "max-h-[60vh]" : "min-h-0 flex-1",
            )}
          >
            <TableHeader className="sticky top-0 z-10 bg-background">
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    aria-label="Select all inventories"
                    checked={
                      allVisibleInventoriesSelected
                        ? true
                        : someVisibleInventoriesSelected
                          ? "indeterminate"
                          : false
                    }
                    onCheckedChange={(checked) =>
                      toggleAllVisibleInventoriesSelection(checked === true)
                    }
                    onClick={(event) => event.stopPropagation()}
                  />
                </TableHead>
                <TableHead>
                  <SortableHeader
                    label="Product"
                    sortKey="productName"
                    sortState={sortState}
                    onSort={handleSort}
                  />
                </TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>2FA Status</TableHead>
                <TableHead>Payment Arrangement</TableHead>
                <TableHead>
                  <SortableHeader
                    label="Account Tier"
                    sortKey="accountTier"
                    sortState={sortState}
                    onSort={handleSort}
                  />
                </TableHead>
                <TableHead>User ID / Username</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Booking Ending</TableHead>
                <TableHead className="text-right">
                  <SortableHeader
                    label="Buy CPM (¢)"
                    sortKey="buyCpm"
                    sortState={sortState}
                    onSort={handleSort}
                  />
                </TableHead>
                <TableHead className="text-right">Purchased</TableHead>
                <TableHead className="text-right">Sold</TableHead>
                <TableHead className="text-right">
                  <SortableHeader
                    label="Available"
                    sortKey="availableMiles"
                    sortState={sortState}
                    onSort={handleSort}
                  />
                </TableHead>
                <TableHead className="w-[120px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {inventoriesQuery.isLoading || inventoriesQuery.hasNextPage ? (
                <TableRow>
                  <TableCell
                    colSpan={14}
                    className="text-center text-muted-foreground"
                  >
                    Loading inventories...
                  </TableCell>
                </TableRow>
              ) : sortedInventories.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={14}
                    className="text-center text-muted-foreground"
                  >
                    No inventories found.
                  </TableCell>
                </TableRow>
              ) : (
                sortedInventories.map((inventory) => (
                  <TableRow
                    key={inventory.id}
                    className="cursor-pointer"
                    onClick={() =>
                      setSearchParams(
                        { inventoryId: inventory.id },
                        { replace: true },
                      )
                    }
                  >
                    <TableCell onClick={(event) => event.stopPropagation()}>
                      <Checkbox
                        aria-label={`Select inventory ${inventory.name}`}
                        checked={selectedInventoryIds.has(inventory.id)}
                        onCheckedChange={(checked) =>
                          toggleInventorySelection(
                            inventory.id,
                            checked === true,
                          )
                        }
                      />
                    </TableCell>
                    <TableCell title={inventory.productName}>
                      <div>
                        {productCodeById.get(inventory.productId) ||
                          abbreviateProductName(inventory.productName)}
                      </div>
                    </TableCell>
                    <TableCell>{inventory.accountOwnerName || "-"}</TableCell>
                    <TableCell>
                      {formatStatusLabel(inventory.twoFaStatus)}
                    </TableCell>
                    <TableCell>
                      {formatStatusLabel(
                        inventoryPaymentTimingMap.get(inventory.id),
                      ) || "-"}
                    </TableCell>
                    <TableCell>{inventory.statusTier?.name || "-"}</TableCell>
                    <TableCell>{inventory.username || "-"}</TableCell>
                    <TableCell>
                      {formatStatusLabel(
                        getInventoryOperationalStatus({ inventory }),
                      )}
                    </TableCell>
                    <TableCell>
                      {inventory.bookingBlockedUntil
                        ? timestampToDate(
                            inventory.bookingBlockedUntil,
                          )?.toLocaleDateString()
                        : "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatBuyCpm(inventoryBuyCpmMap.get(inventory.id))}
                    </TableCell>
                    <TableCell className="text-right">
                      {inventory.totalPurchasedMiles.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      {inventory.totalSoldMiles.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      {inventory.availableMiles.toLocaleString()}
                    </TableCell>
                    <TableCell onClick={(event) => event.stopPropagation()}>
                      <Button asChild variant="ghost" size="sm">
                        <Link
                          to={buildInventoryWorkspacePath({
                            inventoryId: inventory.id,
                          })}
                        >
                          Workspace
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <InventorySheet
        inventoryId={selectedInventoryId}
        open={selectedInventoryId.length > 0}
        onOpenChange={(open) => {
          if (!open) {
            setSearchParams(
              (previous) => {
                const next = new URLSearchParams(previous);
                next.delete("inventoryId");
                return next;
              },
              { replace: true },
            );
          }
        }}
      />
    </div>
  );
}
